import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import {
  ChevronLeft, Calendar, MapPin, Stethoscope, CheckCircle2, XCircle,
  HelpCircle, CalendarCheck2, Archive, Edit3, ArchiveRestore,
} from "lucide-react";
import { api, getUser } from "../lib/api";
import { Page, Spinner, UrgencyChip, StatusChip } from "../components/Brand";
import { formatDateFr, add30 } from "../lib/fr";

export default function OBGYNManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = getUser();

  const { data: ref, isLoading } = useQuery({
    queryKey: ["referral", id],
    queryFn: async () => (await api.get(`/referrals/${id}`)).data,
  });

  if (isLoading) return <Page><div className="cs-card p-10 flex justify-center"><Spinner /></div></Page>;
  if (!ref) return <Page><div>Référence introuvable</div></Page>;

  const isScheduled = !!ref.appointment_date;

  async function updateStatus(status, label) {
    try {
      await api.post(`/referrals/${id}/status`, { status });
      toast.success(`Statut: ${label}`);
      queryClient.invalidateQueries({ queryKey: ["referral", id] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-inbox"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function archive() {
    if (!window.confirm("Archiver cette référence ? Elle disparaîtra de l'agenda et de la boîte de réception.")) return;
    try {
      await api.post(`/referrals/${id}/archive`);
      toast.success("Référence archivée");
      queryClient.invalidateQueries({ queryKey: ["obgyn-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["my-referrals"] });
      setTimeout(() => navigate(me?.role === "im" ? "/im" : "/obgyn/agenda"), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function unarchive() {
    try {
      await api.post(`/referrals/${id}/unarchive`);
      toast.success("Référence restaurée");
      queryClient.invalidateQueries({ queryKey: ["referral", id] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate(-1)} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Retour
      </button>

      <section className="cs-card p-5 md:p-6 mb-6" data-testid="manage-summary">
        <div className="flex items-start justify-between pb-3 border-b" style={{ borderColor: "var(--cs-border)" }}>
          <div>
            <div className="cs-label">Référence</div>
            <div className="font-semibold mt-1" style={{ fontSize: 26, letterSpacing: "-0.5px" }}>{ref.referral_code}</div>
            {ref.archived && (
              <div className="mt-2"><span className="cs-chip" style={{ background: "var(--cs-bg)", color: "var(--cs-text-muted)", borderColor: "var(--cs-border)" }}>Archivée</span></div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <UrgencyChip value={ref.urgency} />
            <StatusChip value={ref.status} />
          </div>
        </div>

        {isScheduled && (
          <div className="mt-5 p-4 rounded-[12px]" style={{ background: "rgba(124,179,66,0.10)", borderLeft: "4px solid var(--cs-primary)" }}>
            <div className="cs-label" style={{ color: "var(--cs-primary-dark)" }}>Rendez-vous</div>
            <div className="font-semibold mt-2 text-[16px]">
              <Calendar size={14} className="inline mr-1" /> {formatDateFr(ref.appointment_date)}
            </div>
            <div className="text-[14px] mt-0.5">{ref.appointment_time} → {add30(ref.appointment_time)} (30 min)</div>
            <div className="text-[13px] mt-2 flex flex-wrap gap-3" style={{ color: "var(--cs-text-muted)" }}>
              <span><MapPin size={12} className="inline" /> {ref.location}</span>
              <span><Stethoscope size={12} className="inline" /> {ref.provider}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-4 pt-4 text-[13px]">
          <Field label="Diagnostic" value={ref.diagnosis === "cardiomyopathy" ? "Cardiomyopathie" : "CMP péripartum"} />
          <Field label="FEVG" value={ref.ef_range + " %"} />
          <Field label="Âge" value={ref.age_range + " ans"} />
          <Field label="Grossesse" value={pregLabel(ref.pregnancy_status)} />
          <Field label="Référent" value={ref.created_by_name || "—"} />
          <Field label="Œstrogènes" value={ref.estrogen_contraindicated ? "Contre-indiqués" : "Autorisés"} />
        </div>
      </section>

      {/* Status actions — only if scheduled */}
      {isScheduled && !ref.archived && (
        <section className="cs-card p-5 md:p-6 mb-6" data-testid="status-actions">
          <h2 className="font-semibold mb-1" style={{ fontSize: 18, letterSpacing: "-0.3px" }}>
            Marquer le rendez-vous
          </h2>
          <p className="text-[13px] mb-4" style={{ color: "var(--cs-text-muted)" }}>
            Mettez à jour le statut une fois le rendez-vous passé. Cela notifie automatiquement la Médecine Interne.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button onClick={() => updateStatus("seen", "Vue")} className="cs-btn-ghost" data-testid="status-seen">
              <CheckCircle2 size={16} /> Marquer Vue
            </button>
            <button onClick={() => updateStatus("missed", "Manqué")} className="cs-btn-danger-outline" data-testid="status-missed">
              <XCircle size={16} /> Marquer Manqué
            </button>
            <button onClick={() => updateStatus("need_info", "Info requise")} className="cs-btn-ghost" data-testid="status-need-info">
              <HelpCircle size={16} /> Demander info
            </button>
            <button onClick={() => updateStatus("completed", "Terminé")} className="cs-btn-primary" data-testid="status-completed">
              <CalendarCheck2 size={16} /> Marquer Terminé
            </button>
          </div>
        </section>
      )}

      {/* Re-schedule / archive */}
      <section className="cs-card p-5 md:p-6" data-testid="actions">
        <h2 className="font-semibold mb-4" style={{ fontSize: 18, letterSpacing: "-0.3px" }}>Autres actions</h2>
        <div className="flex flex-wrap gap-3">
          {!ref.archived && me?.role === "obgyn" && (
            <button onClick={() => navigate(`/obgyn/${id}`)} className="cs-btn-ghost" data-testid="reschedule">
              <Edit3 size={16} /> {isScheduled ? "Reprogrammer" : "Programmer"}
            </button>
          )}
          {!ref.archived ? (
            <button onClick={archive} className="cs-btn-danger-outline" data-testid="archive-referral">
              <Archive size={16} /> Archiver la référence
            </button>
          ) : (
            <button onClick={unarchive} className="cs-btn-ghost" data-testid="unarchive-referral">
              <ArchiveRestore size={16} /> Restaurer
            </button>
          )}
        </div>
      </section>
    </Page>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="cs-label">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
function pregLabel(v) {
  return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v;
}
